import {EventEmitter, Injectable} from '@angular/core';
import {UserService} from '../../shared/user.service';
import {CurrentDayService} from '../../shared/current-day.service';
import {Observable} from 'rxjs/Observable';
import {ApiService} from '../../api/api.service';
import {CookieService} from 'ngx-cookie';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/observable/of';
import {Notifications} from './notifications.enum';
import {BillingCodes} from '../../shared/billing-codes.enum';
import {Employee} from 'resteasytox';
import * as moment from 'moment';

interface DaysOffRequestDateRange {
	firstName: string;
	lastName: string;
	employeeId: number;
	dates: any[];
	daysAmount: number;
}
type NotificationName = 'hideMissingWorkingDaysNotification' | 'hideOnCallNotification' | 'hideDaysOffNotification' | 'hideWorkingTimeNotification';

@Injectable()
export class NotificationsService {
	dayOffApproved$: EventEmitter<any> = new EventEmitter<any>();

	static cookieNameFromType(type: Notifications): string {
		switch (type) {
			case Notifications.MISSING_WORKING_DAYS:
				return 'showMissingWorkingDaysNotifications';
			case Notifications.ON_CALL:
				return 'showOnCallNotification';
			case Notifications.APPROVED_DAYS_OFF:
				return 'showApprovedDaysOffNotification';
			case Notifications.WORKING_TIME:
				return 'hideWorkingTimeNotification';
			case Notifications.UNAPPROVED_DAYS_OFF:
				return 'showDayOffRequestNotification';
			case Notifications.EMPLOYEE_UNSUBMITTED_TIMECARD_NOTIFICATION:
				return 'employeeUnsubmittedTimecardNotification';
			default:
				return null;
		}
	}

	static settingNameFromType(type: Notifications): NotificationName  {
		switch (type) {
			case Notifications.MISSING_WORKING_DAYS:
				return 'hideMissingWorkingDaysNotification';
			case Notifications.ON_CALL:
				return 'hideOnCallNotification';
			case Notifications.APPROVED_DAYS_OFF:
				return 'hideDaysOffNotification';
			case Notifications.WORKING_TIME:
				return 'hideWorkingTimeNotification';
			default:
				return null;
		}
	}

	static getWeekNumber(d: number | Date): number {
		// Copy date so don't modify original
		const date: Date = new Date(+d);
		date.setHours(0, 0, 0);
		// Set to nearest Thursday: current date + 4 - current day number
		// Make Sunday's day number 7
		date.setDate(date.getDate() + 4 - (date.getDay() || 7));
		// Get first day of year
		const yearStart: Date = new Date(date.getFullYear(), 0, 1);
		// Calculate full weeks to nearest Thursday
		return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
	}

	static findScheduledOnCallEmployee(calendar: any[], date: number | Date): Employee {
		const todayWeekNumber: number = NotificationsService.getWeekNumber(date);
		let employee: Employee = null;
		for (let i: number = 0; i < calendar.length; i++) {
			const item: any = calendar[i];
			if (item.weekNumber === todayWeekNumber) {
				employee = item.employee;
				break;
			}
		}
		return employee;
	}

	static flattenMissingWorkingDays(missingWorkingDays: any): any {
		const result: any[] = [];
		for (const user in missingWorkingDays) {
			if (missingWorkingDays.hasOwnProperty(user)) {
				for (const date of missingWorkingDays[user]) {
					result.push(date);
				}
			}
		}
		return result;
	}

	constructor(private apiService: ApiService,
				private userService: UserService,
				private currentDayService: CurrentDayService,
				private cookieService: CookieService) {
	}

	getMissingWorkingDays(startDate?: string, endDate?: string): Observable<any> {
		const year: number = this.currentDayService.currentDay.getFullYear();
		if (this.userService.user) {
			return this.apiService
				.getMissingWorkingDays({
					employees: [this.userService.user.id],
					startDate: startDate || '01/01/' + year,
					endDate: endDate || '12/31/' + year,
					includeToday: false
				})
				.map(NotificationsService.flattenMissingWorkingDays);
		}
	}

	getOnCallEmployee(): Observable<any> {
		let calendar: any;
		const today: Date = this.currentDayService.currentDay;
		return this.apiService
			.getOnCallCalendar({year: this.currentDayService.currentDay.getFullYear()})
			.flatMap((response: any) => {
				calendar = response;
				return this.apiService.getDailyDetailsTree({
					day: today.getDate(),
					month: today.getMonth() + 1,
					year: today.getFullYear(),
					disableLoader: true,
					billingCodes: [BillingCodes.ON_CALL],
				});
			})
			.map((response: any) => {
				let employee: Employee;
				for (let i: number = 0; i < response.length; i++) {
					const item: any = response[i];
					if (item.data.length > 0) {
						employee = item.employeeDetails;
						break;
					}
				}

				if (!employee) {
					employee = NotificationsService.findScheduledOnCallEmployee(calendar, today);
				}

				return employee;
			});
	}

	getApprovedDaysOff(): Observable<any> {
		const today: Date = new Date(),
			endPeriod: Date = new Date();
		endPeriod.setDate(endPeriod.getDate() + 30);

		return this.apiService
			.getApprovedDaysOffRequests({startDate: today, endDate: endPeriod})
			.map((ranges: any) => this.mergeDaysOffRequests(ranges));
	}

	getUnapprovedDayOffRequests(): Observable<any> {
		return this.apiService
			.getUnapprovedDaysOffRequests()
			.map((ranges: any) => this.mergeDaysOffRequests(ranges));
	}

	approveDayOffRequest(range: any): void {
		this.apiService
			.approveDayOffRequest({
				ids: range.dates.map((entry: any) => entry.id)
			})
			.subscribe(() => {

				this.dayOffApproved$.emit();
			});
	}

	mergeDaysOffRequests(data: any[]): any[] {
		if (data.length === 0) {
			return data;
		}

		const hour: number = 60 * 60 * 1000;
		const result: any[] = [];
		let datesRange: DaysOffRequestDateRange = {
			firstName: data[0].firstName,
			lastName: data[0].lastName,
			employeeId: data[0].employeeId,
			dates: [],
			daysAmount: 0
		};

		data.forEach((dayOff: any) => {
			const firstName: string = dayOff.firstName;
			const lastName: string = dayOff.lastName;
			const employeeId: number = dayOff.employeeId;
			if (datesRange.firstName === firstName && datesRange.lastName === lastName) {
				if (datesRange.dates.length === 0) {
					datesRange.dates.push(dayOff);
					datesRange.daysAmount = 1;
				} else {
					const lastDate: number = datesRange.dates[datesRange.dates.length - 1].date;
					if ((dayOff.date - lastDate) / hour <= 24) {
						datesRange.dates[1] = dayOff;
						datesRange.daysAmount++;
					} else {
						result.push(datesRange);
						datesRange = {
							firstName: firstName,
							lastName: lastName,
							employeeId: employeeId,
							dates: [dayOff],
							daysAmount: 1
						};
					}
				}
			} else {
				result.push(datesRange);
				datesRange = {
					firstName: firstName,
					lastName: lastName,
					employeeId: employeeId,
					dates: [dayOff],
					daysAmount: 1
				};
			}
		});

		result.push(datesRange); // the last datesRange

		return result;
	}

	getDailyDetailsForEmployee(): Observable<any> {
		const now: Date = new Date();
		return this.apiService.getDailyDetailsForEmployee({
			year: now.getFullYear(),
			month: now.getMonth() + 1,
			day: now.getDate(),
			id: this.userService.user.id
		});
	}

	getTimeCardsWaitingForApproval(): Observable<any> {
		return this.apiService.getTimeCardsWaitingForApproval();
	}

	isNotificationAvailable(type: Notifications): boolean {
		if (this.userService.user) {

			if (type === Notifications.WORKING_TIME) {
				return !this.userService.checkUserSetting(NotificationsService.settingNameFromType(type))
					&& !this.cookieService.getObject(NotificationsService.cookieNameFromType(type));
			}

			return !this.userService.checkUserSetting(NotificationsService.settingNameFromType(type)) && this.cookieService.getObject(NotificationsService.cookieNameFromType(type)) !== false;
		}

		return false;
	}

	closeNotification(type: Notifications): void {
		const cookieName: string = NotificationsService.cookieNameFromType(type);
		if (cookieName) {
			this.cookieService.put(cookieName,
				type === Notifications.WORKING_TIME ? 'true' : 'false',
				{
					expires: moment().add(1, 'days').toDate()
				}
			);
		}
	}

	isPartialDaysOffRange(range: any): boolean {
		let partial: boolean = true;
		for (let i: number = 0; i < range.dates.length; i++) {
			if (range.dates[i].hours >= 8) {
				partial = false;
				break;
			}
		}
		return partial;
	}
}



// WEBPACK FOOTER //
// ./src/app/my-time/notifications/notifications.service.ts